import store, { RootState, storeDispatch } from "@/redux/store";
import { setLoading, setError as setFeatureFlagsError } from "@/redux/featureFlagsSlice";
import FeatureFlagService from "@/services/feature-flag/FeatureFlagService";
import { FeatureFlagNames } from "@/central-atoms/feature-flags/enums";
import { FeatureFlagAvailability } from "@/central-atoms/feature-flags/types";
import { isEmpty } from "lodash";

/**
 * Initializes the application by fetching feature flags and setting up any required data
 * @returns Promise that resolves when initialization is complete
 */
export const initializeFeatureFlag = async (_activeFleetId?: string): Promise<void> => {
  try {
    // Get the active fleet ID from localStorage or other source
    let activeFleetId = _activeFleetId;
    if (!activeFleetId) {
      activeFleetId = getActiveFleetId();
    }
    const orgIds = activeFleetId ? [activeFleetId] : [];
    
    // Initialize feature flags
    storeDispatch(setLoading());
    await FeatureFlagService.fetchAndApplyFeatureFlags(orgIds);
  } 
  catch (error) {
    console.error('Failed to initialize feature flags:', error);
    storeDispatch(setFeatureFlagsError('Failed to initialize application'));
    throw error;
  }
};

/**
 * Stores the active fleet ID in localStorage for future use
 * @param fleetId The fleet ID to store
 */
export const storeActiveFleetId = (fleetId: string): void => {
  if (fleetId) {
    localStorage.setItem('active_fleet_id', fleetId);
  }
}; 

export const removeActiveFleetId = (): void => {
  localStorage.removeItem('active_fleet_id');
};

export const getActiveFleetId = (): string => {
  try {
    let activeFleetId = localStorage.getItem('active_fleet_id') || '';

    if (!activeFleetId) {
      const driverDetails = store.getState()?.driverDetails.details?.data;
      if(driverDetails?.user?.active_fleet_id) {
        activeFleetId = driverDetails?.user?.active_fleet_id;
        storeActiveFleetId(activeFleetId);
      }
    }
    return activeFleetId;
  } catch (error) {
    console.error('Failed to get active fleet ID:', error);
    return '';
  }
};

export function canViewFeature(featureFlagName: FeatureFlagNames, chargePoints: string[] = [], chargingStations: string[] = [], platforms: string[] = []): boolean {
    
  const state = store.getState();
  const featureFlags = state.featureFlags.featureFlags;
  const featureFlagAvailability = featureFlags[featureFlagName] as FeatureFlagAvailability;
  
  if (featureFlagAvailability) {
      // Default case
      if (isEmpty(chargePoints) && isEmpty(chargingStations) && isEmpty(platforms)) {
          return true;
      }
      if (!isEmpty(chargePoints) && doArraysOverlap(chargePoints, featureFlagAvailability.availableForChargePoints || [])) {
          return true;
      }
      if (!isEmpty(chargingStations) && doArraysOverlap(chargingStations, featureFlagAvailability.availableForChargingStations || [])) {
          return true;
      }
      if (!isEmpty(platforms) && doArraysOverlap(platforms, featureFlagAvailability.availableForPlatforms || [])) {
          return true;
      }
      return false;
  }
  return false;
}

export function doArraysOverlap(arr1: any[], arr2: any[]): boolean {
  return arr1.some((arr1Item) => {
      return arr2.includes(arr1Item);
  });
}